import React, { useState, useEffect } from 'react';
import { fetchLevels as fetchLevelsAPI, fetchSubjects as fetchSubjectsAPI, fetchStreams as fetchStreamsAPI, fetchProfile, deleteLevel, deleteStream, deleteSubject } from '../utils/api';
import { ADMIN_ROLE, ACADEMIC_COORDINATOR_STAFF, STUDENT_SERVICES_STAFF } from '../utils/roles';
import { mapErrorMessage } from '../utils/errorMapping';
import CreateLevelModal from './CreateLevelModal';
import EditLevelModal from './EditLevelModal';
import CreateSubjectModal from './CreateSubjectModal';
import EditSubjectModal from './EditSubjectModal';
import CreateStreamModal from './CreateStreamModal';
import EditStreamModal from './EditStreamModal';

import './LSS.css';

const LSS = () => {
    const [levels, setLevels] = useState([]);
    const [totalLevels, setTotalLevels] = useState(0);
    const [subjects, setSubjects] = useState([]);
    const [totalSubjects, setTotalSubjects] = useState(0);
    const [streams, setStreams] = useState([]);
    const [totalStreams, setTotalStreams] = useState(0);

    const [error, setError] = useState('');
    const [isCreateLevelModalOpen, setCreateLevelModalOpen] = useState(false);
    const [isEditLevelModalOpen, setEditLevelModalOpen] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(null);

    const [isCreateSubjectModalOpen, setCreateSubjectModalOpen] = useState(false);
    const [isEditSubjectModalOpen, setEditSubjectModalOpen] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null);

    const [isCreateStreamModalOpen, setCreateStreamModalOpen] = useState(false);
    const [isEditStreamModalOpen, setEditStreamModalOpen] = useState(false);
    const [selectedStream, setSelectedStream] = useState(null);

    const [canCreate, setCanCreate] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [activeTab, setActiveTab] = useState('levels');

    const fetchLevels = async () => {
        try {
            const data = await fetchLevelsAPI();
            setLevels(data.data || []);
            setTotalLevels(data.total || 0);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const fetchSubjects = async () => {
        try {
            const data = await fetchSubjectsAPI();
            setSubjects(data.data || []);
            setTotalSubjects(data.total || 0);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    const fetchStreams = async () => {
        try {
            const data = await fetchStreamsAPI();
            setStreams(data.data || []);
            setTotalStreams(data.total || 0);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    useEffect(() => {
        const checkPermissions = async () => {
            try {
                const profileData = await fetchProfile();
                const userRoles = profileData.data.roles || [];
                const isAdmin = userRoles.some(role => role.id === ADMIN_ROLE);
                const isAcademicCoordinator = userRoles.some(role => role.id === ACADEMIC_COORDINATOR_STAFF);
                const isStudentServices = userRoles.some(role => role.id === STUDENT_SERVICES_STAFF);
                setCanCreate(isAdmin || isAcademicCoordinator);
                setCanUpdate(isAdmin || isAcademicCoordinator || isStudentServices);
                setCanDelete(isAdmin || isAcademicCoordinator);
            } catch (error) {
                console.error("Could not fetch profile for permissions check", error);
            }
        };

        fetchLevels();
        fetchSubjects();
        fetchStreams();
        checkPermissions();
    }, []);

    const handleLevelCreated = () => {
        fetchLevels();
        setCreateLevelModalOpen(false);
    };

    const handleLevelUpdated = () => {
        fetchLevels();
        setEditLevelModalOpen(false);
    };

    const openEditLevelModal = (level) => {
        setSelectedLevel(level);
        setEditLevelModalOpen(true);
    };

    const handleSubjectCreated = () => {
        fetchSubjects();
        setCreateSubjectModalOpen(false);
    };

    const handleSubjectUpdated = () => {
        fetchSubjects();
        setEditSubjectModalOpen(false);
    };

    const openEditSubjectModal = (subject) => {
        setSelectedSubject(subject);
        setEditSubjectModalOpen(true);
    };

    const handleStreamCreated = () => {
        fetchStreams();
        setCreateStreamModalOpen(false);
    };

    const handleStreamUpdated = () => {
        fetchStreams();
        setEditStreamModalOpen(false);
    };

    const openEditStreamModal = (stream) => {
        setSelectedStream(stream);
        setEditStreamModalOpen(true);
    };

    const handleDeleteLevel = async (levelId) => {
        if (window.confirm('Are you sure you want to delete this level? All associated classes and groups will also be deleted.')) {
            try {
                await deleteLevel(levelId);
                fetchLevels();
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        }
    };

    const handleDeleteStream = async (streamId) => {
        if (window.confirm('Are you sure you want to delete this stream? All associated groups will also be deleted.')) {
            try {
                await deleteStream(streamId);
                fetchStreams();
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        }
    };

    const handleDeleteSubject = async (subjectId) => {
        if (window.confirm('Are you sure you want to delete this subject? All associated groups will also be deleted.')) {
            try {
                await deleteSubject(subjectId);
                fetchSubjects();
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        }
    };

    const renderLevels = () => (
        <div>
            <div className="header">
                <h1>Levels | {totalLevels}</h1>
                {canCreate && (
                    <button onClick={() => setCreateLevelModalOpen(true)} className="add-student-button">+</button>
                )}
            </div>
            {isCreateLevelModalOpen && (
                <CreateLevelModal
                    onClose={() => setCreateLevelModalOpen(false)}
                    onLevelCreated={handleLevelCreated}
                />
            )}
            {isEditLevelModalOpen && (
                <EditLevelModal
                    level={selectedLevel}
                    onClose={() => setEditLevelModalOpen(false)}
                    onLevelUpdated={handleLevelUpdated}
                />
            )}
            <table className="lss-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        {(canUpdate || canDelete) && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {levels.map(level => (
                        <tr key={level.id}>
                            <td>{level.name}</td>
                            {(canUpdate || canDelete) && (
                                <td>
                                    {canUpdate && <button onClick={() => openEditLevelModal(level)} className='submit-button text-button margin-right-button'>Update</button>}
                                    {canDelete && <button onClick={() => handleDeleteLevel(level.id)} className='submit-button text-button margin-right-button'>Delete</button>}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderSubjects = () => (
        <div>
            <div className="header">
                <h1>Subjects | {totalSubjects}</h1>
                {canCreate && (
                    <button onClick={() => setCreateSubjectModalOpen(true)} className="add-student-button">+</button>
                )}
            </div>
            {isCreateSubjectModalOpen && (
                <CreateSubjectModal
                    onClose={() => setCreateSubjectModalOpen(false)}
                    onSubjectCreated={handleSubjectCreated}
                />
            )}
            {isEditSubjectModalOpen && (
                <EditSubjectModal
                    subject={selectedSubject}
                    onClose={() => setEditSubjectModalOpen(false)}
                    onSubjectUpdated={handleSubjectUpdated}
                />
            )}
            <table className="lss-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        {(canUpdate || canDelete) && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {subjects.map(subject => (
                        <tr key={subject.id}>
                            <td>{subject.name}</td>
                            {(canUpdate || canDelete) && (
                                <td>
                                    {canUpdate && <button onClick={() => openEditSubjectModal(subject)} className='submit-button text-button margin-right-button'>Update</button>}
                                    {canDelete && <button onClick={() => handleDeleteSubject(subject.id)} className='submit-button text-button margin-right-button'>Delete</button>}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderStreams = () => (
        <div>
            <div className="header">
                <h1>Streams | {totalStreams}</h1>
                {canCreate && (
                    <button onClick={() => setCreateStreamModalOpen(true)} className="add-student-button">+</button>
                )}
            </div>
            {isCreateStreamModalOpen && (
                <CreateStreamModal
                    onClose={() => setCreateStreamModalOpen(false)}
                    onStreamCreated={handleStreamCreated}
                />
            )}
            {isEditStreamModalOpen && (
                <EditStreamModal
                    stream={selectedStream}
                    onClose={() => setEditStreamModalOpen(false)}
                    onStreamUpdated={handleStreamUpdated}
                />
            )}
            <table className="lss-table">
                <thead>
                    <tr>
                        <th>Name</th>
                        {(canUpdate || canDelete) && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {streams.map(stream => (
                        <tr key={stream.id}>
                            <td>{stream.name}</td>
                            {(canUpdate || canDelete) && (
                                <td>
                                    {canUpdate && <button onClick={() => openEditStreamModal(stream)} className='submit-button text-button margin-right-button'>Update</button>}
                                    {canDelete && <button onClick={() => handleDeleteStream(stream.id)} className='submit-button text-button margin-right-button'>Delete</button>}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );


    return (
        <div className="invoices-container">
            <div className="lss-tabs">
                <button onClick={() => setActiveTab('levels')} className={activeTab === 'levels' ? 'active' : ''}>Levels</button>
                <button onClick={() => setActiveTab('streams')} className={activeTab === 'streams' ? 'active' : ''}>Streams</button>
                <button onClick={() => setActiveTab('subjects')} className={activeTab === 'subjects' ? 'active' : ''}>Subjects</button>
            </div>
            {error && <p className="error">{error}</p>}
            <div>
                {activeTab === 'levels' && renderLevels()}
                {activeTab === 'streams' && renderStreams()}
                {activeTab === 'subjects' && renderSubjects()}
            </div>
        </div>
    );
};

export default LSS;
